import React, { useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PrivacyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lastUpdated = "January 24, 2026";

  // Scroll to section if hash is present
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const sections = [
    { id: 'controller', title: '1. Data Controller' },
    { id: 'data-collected', title: '2. Personal data collected' },
    { id: 'purpose', title: '3. Purpose of processing' },
    { id: 'ai-processing', title: '4. AI-assisted processing' },
    { id: 'data-sharing', title: '5. Data sharing' },
    { id: 'security', title: '6. Data security' },
    { id: 'retention', title: '7. Data retention' },
    { id: 'rights', title: '8. User rights' },
    { id: 'cookies', title: '9. Cookies' },
    { id: 'contact', title: '10. Contact' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/')} className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Home</span>
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-gray-800">Privacy Policy</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8 md:py-12">
        <div className="lg:flex lg:gap-8">
          {/* Sidebar Table of Contents - Desktop */}
          <aside className="hidden lg:block lg:w-64 flex-shrink-0">
            <div className="sticky top-24 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-800 mb-4 text-sm uppercase tracking-wide">Table of Contents</h3>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
            {/* Title */}
            <div className="text-center mb-10 pb-8 border-b border-gray-200">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
              <p className="text-gray-500">Last updated: {lastUpdated}</p>
            </div>

            {/* Mobile Table of Contents */}
            <div className="lg:hidden mb-8">
              <details className="bg-gray-50 rounded-lg border border-gray-200">
                <summary className="px-4 py-3 cursor-pointer font-semibold text-gray-800">
                  Table of Contents
                </summary>
                <nav className="px-4 pb-4 space-y-1">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      {section.title}
                    </button>
                  ))}
                </nav>
              </details>
            </div>

            {/* Introduction */}
            <section className="mb-12">
              <p className="text-gray-700 leading-relaxed">
                This Privacy Policy explains how personal data is collected and processed when using SnowExchange. It should be read together with the <Link to="/terms" className="text-blue-600 hover:underline">Terms of Use</Link> and the <Link to="/ai-disclaimer" className="text-blue-600 hover:underline">AI Disclaimer</Link>.
              </p>
            </section>

            {/* 1. Data Controller */}
            <section id="controller" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Data Controller</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  The data controller is the legal entity currently operating the SnowExchange platform, as identified on the Platform or in the applicable legal notice.
                </p>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p><strong>Email:</strong> <a href="mailto:support@snow-exchange.com" className="text-blue-600 hover:underline">support@snow-exchange.com</a></p>
                </div>
              </div>
            </section>

            {/* 2. Personal data collected */}
            <section id="data-collected" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Personal data collected</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200 space-y-4">
                  <div>
                    <p className="font-semibold text-gray-800">Account data</p>
                    <p className="text-sm">name, email, professional role, company details</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Profile data</p>
                    <p className="text-sm">CVs, certifications, skills, availability</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Technical data</p>
                    <p className="text-sm">IP address, device, browser, cookies</p>
                  </div>
                </div>
              </div>
            </section>

            {/* 3. Purpose of processing */}
            <section id="purpose" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Purpose of processing</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <ul className="list-disc pl-6 space-y-2">
                  <li>Operating the Platform and enabling matchmaking</li>
                  <li>Communication and notifications</li>
                  <li>Platform improvement and security</li>
                  <li>Legal and regulatory compliance</li>
                </ul>
              </div>
            </section>

            {/* 4. AI-assisted processing */}
            <section id="ai-processing" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. AI-assisted processing</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  SnowExchange uses AI-based tools to assist with matching and recommendations. These tools do not perform automated decision-making with legal effect. Details are provided in the <Link to="/ai-disclaimer" className="text-blue-600 hover:underline">AI Disclaimer</Link>.
                </p>
              </div>
            </section>

            {/* 5. Data sharing */}
            <section id="data-sharing" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data sharing</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Data may be shared with:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>other Users (based on visibility settings),</li>
                  <li>partner companies involved in collaboration facilitation,</li>
                  <li>technical service providers,</li>
                  <li>authorities when legally required.</li>
                </ul>
              </div>
            </section>

            {/* 6. Data security */}
            <section id="security" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Data security</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Appropriate technical and organisational measures are implemented, without guaranteeing absolute security.
                </p>
              </div>
            </section>

            {/* 7. Data retention */}
            <section id="retention" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Data retention</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Data is retained for as long as necessary for the purposes described, and deleted or anonymised when no longer required, subject to legal obligations.
                </p>
              </div>
            </section>

            {/* 8. User rights */}
            <section id="rights" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">8. User rights</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Users have rights of access, rectification, erasure, restriction, portability, and objection, in accordance with applicable data protection laws.
                </p>
                <p>
                  Requests may be sent to <a href="mailto:support@snow-exchange.com" className="text-blue-600 hover:underline">support@snow-exchange.com</a>.
                </p>
              </div>
            </section>

            {/* 9. Cookies */}
            <section id="cookies" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Cookies</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  The Platform uses essential and analytics cookies. Users may control cookies via browser settings.
                </p>
              </div>
            </section>

            {/* 10. Contact */}
            <section id="contact" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Contact</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                  <p><strong>Privacy questions:</strong> <a href="mailto:support@snow-exchange.com" className="text-blue-600 hover:underline">support@snow-exchange.com</a></p>
                </div>
              </div>
            </section>

            {/* Related Documents */}
            <div className="border-t border-gray-200 pt-8 mt-12">
              <h3 className="font-semibold text-gray-800 mb-4">Related Documents</h3>
              <div className="flex flex-wrap gap-4">
                <Link to="/terms" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors">
                  Terms of Use
                </Link>
                <Link to="/ai-disclaimer" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors">
                  AI Disclaimer
                </Link>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 pt-8 mt-8 text-center">
              <p className="text-sm text-gray-500">
                © {new Date().getFullYear()} SnowExchange. All rights reserved.
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
